import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import ChatInputAuthCTABanner from '@/components/ChatInput/ChatInputAuthCTABanner';
import ChatInputCreditsBanner from '@/components/ChatInput/ChatInputCreditsBanner';
import ChatInputGhostBanner from '@/components/ChatInput/ChatInputGhostBanner';
import ChatInputMessageContextBanner from '@/components/ChatInput/ChatInputMessageContextBanner';
import ChatInputConversationContextBanner from '@/components/ChatInput/ChatInputConversationContextBanner';
import ChatInputUpgradeBanner from '@/components/ChatInput/ChatInputUpgradeBanner';
import ChatInputLowCreditsBanner from '@/components/ChatInput/ChatInputLowCreditsBanner';
import ChatInputLargePromptBanner from '@/components/ChatInput/ChatInputLargePromptBanner';

// `requires-image-banner` / `requires-audio-banner` were intentionally retired
// — they are now represented as ghost cards in the chat input attachment row
// (see `FileParamGhostCard`).
//
// `ghost-banner` is the legacy ghost-user variant; kept registered for now so
// any stale callsites don't blank-state. New code should emit `auth-cta-banner`
// instead — see `ChatInputAuthCTABanner` and the public-dashboard rollout.
const BANNER_COMPONENTS = {
  'auth-cta-banner': ChatInputAuthCTABanner,
  'ghost-banner': ChatInputGhostBanner,
  'credits-banner': ChatInputCreditsBanner,
  'message-context-banner': ChatInputMessageContextBanner,
  'conversation-context-banner': ChatInputConversationContextBanner,
  'upgrade-banner': ChatInputUpgradeBanner,
  'low-credits-banner': ChatInputLowCreditsBanner,
  'large-prompt-banner': ChatInputLargePromptBanner,
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'column',
  }
});

const ChatInputBanners = ({ banners = [], isDetached = false }) => {
  // Process banners to assign proper levels
  const processedBanners = useMemo(() => {
    if (!banners?.length) return [];

    // The auth-CTA / legacy ghost banner always pins to the top so its
    // sign-up call-to-action stays visible above quieter banners. Only one
    // of the two should be present at a time (auth-cta replaces ghost), but
    // we keep the dedupe lenient just in case.
    const pinnedIndex = banners.findIndex(
      b => b.type === 'auth-cta-banner' || b.type === 'ghost-banner'
    );
    const hasPinned = pinnedIndex !== -1;

    let result = [];
    if (hasPinned) {
      result.push({
        ...banners[pinnedIndex],
        level: 1,
      });
    }

    let currentLevel = hasPinned ? 2 : 1;
    banners.forEach((banner, index) => {
      if (hasPinned && index === pinnedIndex) return;
      result.push({
        ...banner,
        level: currentLevel++,
      });
    });

    return result.sort((a, b) => b.level - a.level);
  }, [banners]);

  if (!processedBanners.length) return null;

  return (
    <View style={styles.container}>
      {processedBanners.map((banner) => {
        const BannerComponent = BANNER_COMPONENTS[banner.type];
        if (!BannerComponent) return null;

        return (
          <BannerComponent
            isDetached={isDetached}
            key={banner.type} // Use banner type as key instead of array index
            level={banner.level}
            {...banner.props}
          />
        );
      })}
    </View>
  );
};

export default React.memo(ChatInputBanners); // Add memo to prevent unnecessary re-renders